import React from 'react';
import { LoaderCircleIcon, ArrowDownToLine } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@epoch-ui/components/Button';
import { QuestionResponses } from '@event-editor/components/QuestionResponses/QuestionResponses';
import { CSV_OPTIONS } from 'utils/csv';
import { useCsvReportDownload } from 'hooks/useCsvReportDownload';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { eventRegistrationDataApi } from '@event-editor/api/registration/getEditEventRegistrationData';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';

import { EmptyQuestionnaireResponses } from '../components';

export const QuestionnaireResponsesSection = (): React.ReactElement => {
  const { id, preEventQuestions } = useEditableEvent();
  const { showToast } = useToaster();

  const { data } = useQuery({
    queryKey: eventEditorQueryKeys.eventRegistration(Number(id)),
    queryFn: () => eventRegistrationDataApi.questionnaireStats(Number(id)),
    enabled: typeof Number(id) === 'number' && Number(id) > 0
  });

  const questionnaireSubmitted = data?.questionnaireSubmitted ?? 0;

  const { loading: csvLoading, triggerCsvReportDownload } = useCsvReportDownload({
    type: CSV_OPTIONS.QUESTIONNAIRE,
    exportParams: id,
    onSuccess: (): void => {
      showToast('CSV downloaded');
    },
    onError: (errorMessage): void => {
      showToast(errorMessage, { variant: ToasterVariant.ERROR });
    }
  });

  return (
    <div>
      <div className="tw-flex tw-items-center tw-justify-between">
        <h2 className="tw-m-0 tw-mb-4 tw-text-xl tw-font-medium tw-leading-7">
          Event questionnaire responses
        </h2>

        <div className="tw-flex tw-gap-2">
          {!!questionnaireSubmitted && (
            <Button
              size="xxs"
              variant="secondary"
              className="tw-rounded-xl"
              disabled={csvLoading}
              onClick={() => triggerCsvReportDownload()}
            >
              {csvLoading ? (
                <LoaderCircleIcon className="tw-mr-1 tw-animate-spin" />
              ) : (
                <ArrowDownToLine size={16} className="tw-mr-1" />
              )}
              <span>CSV</span>
            </Button>
          )}
        </div>
      </div>

      {!questionnaireSubmitted ? (
        <EmptyQuestionnaireResponses />
      ) : (
        <QuestionResponses
          questions={preEventQuestions?.filter((q) => !q.isDeleted) || []}
          responsesTotal={questionnaireSubmitted}
        />
      )}
    </div>
  );
};
